import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import {
  ChevronLeft,
  Calendar,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  AlertCircle,
  FileText,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const EventsDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const canAccess =
    user?.department === "meeting_events" ||
    ["admin", "management", "super_admin"].includes(user?.role);

  useEffect(() => {
    if (!canAccess) {
      toast.error("Accesso negato");
      navigate("/events");
      return;
    }
    fetchDashboard();
  }, [canAccess, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/events/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), "d MMM yyyy HH:mm", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), "d MMM", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-stone-500",
      published: "bg-blue-500",
      in_progress: "bg-amber-500",
      completed: "bg-emerald-500",
      cancelled: "bg-red-500",
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Bozze",
      published: "Pubblicati",
      in_progress: "In Corso",
      completed: "Completati",
      cancelled: "Annullati",
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-pulse text-stone-400">Caricamento...</div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-400">Errore nel caricamento</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/events")}
            className="text-stone-600 hover:text-stone-900"
            data-testid="back-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-outfit text-xl font-bold text-stone-900">
              Dashboard Eventi
            </h1>
            <p className="text-xs text-stone-500">Riepilogo e statistiche</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 text-stone-500 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium">Totale Eventi</span>
            </div>
            <p className="text-2xl font-bold text-stone-900">
              {dashboard.total_events}
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 shadow-sm text-white">
            <div className="flex items-center gap-2 text-emerald-100 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Completati</span>
            </div>
            <p className="text-2xl font-bold">
              {dashboard.status_counts.completed}
            </p>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
          <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-stone-500" />
            Stato Eventi
          </h2>
          <div className="space-y-3">
            {Object.entries(dashboard.status_counts).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`} />
                <span className="flex-1 text-sm text-stone-600">
                  {getStatusLabel(status)}
                </span>
                <span className="font-semibold text-stone-900">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
          <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Prossimi 7 Giorni
          </h2>
          
          {dashboard.upcoming_events.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-4">
              Nessun evento in programma
            </p>
          ) : (
            <div className="space-y-3">
              {dashboard.upcoming_events.map((event) => (
                <div
                  key={event.id}
                  className="border border-stone-100 rounded-lg p-3 hover:bg-stone-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/events`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-stone-900">{event.name}</h3>
                      {event.client_name && (
                        <p className="text-xs text-stone-500">{event.client_name}</p>
                      )}
                    </div>
                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      {formatShortDate(event.date_start)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-stone-500">
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {event.acknowledged_sections}/{event.total_sections} conferme
                    </span>
                  </div>
                  
                  {/* Progress bar for acknowledgments */}
                  {event.total_sections > 0 && (
                    <div className="mt-2 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{
                          width: `${(event.acknowledged_sections / event.total_sections) * 100}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Department Summary */}
        <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
          <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            Riepilogo Reparti
          </h2>
          
          {dashboard.department_summary.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-4">
              Nessun dato disponibile
            </p>
          ) : (
            <div className="space-y-3">
              {dashboard.department_summary.map((dept) => (
                <div key={dept.name} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-stone-700">
                        {dept.name}
                      </span>
                      <span className="text-xs text-stone-500">
                        {dept.acknowledged}/{dept.total}
                      </span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          dept.acknowledged === dept.total && dept.total > 0
                            ? "bg-emerald-500"
                            : "bg-amber-400"
                        }`}
                        style={{
                          width: dept.total > 0
                            ? `${(dept.acknowledged / dept.total) * 100}%`
                            : "0%",
                        }}
                      />
                    </div>
                  </div>
                  {dept.acknowledged === dept.total && dept.total > 0 ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : dept.total - dept.acknowledged > 0 ? (
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
          <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-500" />
            Attività Recenti
          </h2>
          
          {dashboard.recent_activity.length === 0 ? (
            <p className="text-sm text-stone-400 text-center py-4">
              Nessuna attività recente
            </p>
          ) : (
            <div className="space-y-3">
              {dashboard.recent_activity.map((activity, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 pb-3 border-b border-stone-100 last:border-0 last:pb-0"
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-700">
                      <span className="font-medium">{activity.acknowledged_by}</span>
                      {" ha confermato "}
                      <span className="font-medium">{activity.department}</span>
                    </p>
                    <p className="text-xs text-stone-500 truncate">
                      {activity.event_name}
                    </p>
                  </div>
                  <span className="text-xs text-stone-400 flex-shrink-0">
                    {formatDate(activity.acknowledged_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EventsDashboard;
